import { SerializedError, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { LoadingState, Redux } from '../..'

export type NotificationCategory = 'restaurant' | 'menu'

type StateSlice<T> = {
  value: T
  loadingState: LoadingState
  error: SerializedError | null
}

export type Notification = {
  userId: number
  message: string
  category: NotificationCategory
  createdAt?: Date
  read?: boolean
}

type InitialStateType<K> = K extends StateSlice<infer T> ? T : never

function getInitialStateFor<K>(initialState: InitialStateType<K>) {
  return {
    value: initialState,
    loadingState: 'initial' as LoadingState,
    error: null
  } as StateSlice<InitialStateType<K>>
}

export const fetchUserNotifications = createAsyncThunk<Notification[], void, Redux>(
  'api/fetchUserNotifications',
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-notifications/my`)
      const {
        data: { data: notifications }
      } = response

      return notifications
    } catch {
      throw new Error('Failed to fetch notifications')
    }
  }
)

const resto365Notifications = createSlice({
  name: 'resto365Notifications',
  initialState: {
    notifications: getInitialStateFor<StateSlice<Notification[]>>([])
  },
  reducers: {
    markAllRead: state => {
      state.notifications.value = state.notifications.value.map(notification => ({
        ...notification,
        read: true
      }))
    },
    addNotification: (state, action: { payload: Notification }) => {
      state.notifications.value = [action.payload, ...state.notifications.value]
    }
  },
  extraReducers: builder => {
    ;(builder.addCase(fetchUserNotifications.fulfilled, (state, action) => {
      state.notifications.loadingState = 'success'
      state.notifications.value = action.payload
    }),
      builder.addCase(fetchUserNotifications.pending, state => {
        state.notifications.loadingState = 'pending'
      }),
      builder.addCase(fetchUserNotifications.rejected, (state, action) => {
        state.notifications.loadingState = 'error'
        state.notifications.value = []
        state.notifications.error = action.error
      }))
  }
})

export default resto365Notifications.reducer
export const { markAllRead, addNotification } = resto365Notifications.actions
